/**
 * Centralized timezone-aware date helpers — the single source of truth for
 * day-bucketing (which calendar day a task completion or streak belongs to).
 *
 * Everything is computed in the user's LOCAL timezone. The 'en-CA' locale is
 * used only because its date format is exactly YYYY-MM-DD; it carries no
 * geographic meaning. The `timeZone` argument carries the actual locale of the
 * user and is supplied by getDeviceTimeZone() in app code. The explicit
 * `timeZone` parameter exists primarily so tests can pin a zone.
 */

/** The device's current IANA timezone, e.g. 'Europe/Brussels'. */
export function getDeviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** A Date → 'YYYY-MM-DD' in the given (or device) timezone. */
export function toDateStr(date: Date, timeZone: string = getDeviceTimeZone()): string {
  return date.toLocaleDateString('en-CA', { timeZone })
}

/** Today as 'YYYY-MM-DD' in the given (or device) timezone. */
export function todayStr(timeZone: string = getDeviceTimeZone()): string {
  return toDateStr(new Date(), timeZone)
}

/**
 * Shift a 'YYYY-MM-DD' string by N whole days, returning 'YYYY-MM-DD'.
 * Anchored at noon UTC so DST transitions never cause a ±1-day drift.
 */
export function addDays(dateStr: string, n: number): string {
  const anchor = new Date(`${dateStr}T12:00:00Z`)
  if (Number.isNaN(anchor.getTime())) throw new Error(`addDays: invalid date '${dateStr}'`)
  anchor.setUTCDate(anchor.getUTCDate() + n)
  return anchor.toISOString().slice(0, 10)
}
